'use client';

import { format } from 'date-fns';
import { Fragment, forwardRef, useCallback, useContext, useMemo } from 'react';

import { Text } from '../text/text';
import { ToggleCard } from '../toggle-card/toggle-card';
import { DateTimeBlockRootContext } from './context/date-time-block-root.context';
import { useDateTimeBlockDisabledItems } from './hooks/use-disabled-items';
import { dateTimeBlockDayRecipe } from './styles/date-time-block-day.styles';
import type { DateTimeBlockDayPropsInterface } from './types/date-time-block-day.types';

/**
 * `DateTimeBlock.Day` represents the day to be selected
 *
 * @since 10.2.0
 */
export const DateTimeBlockDay = forwardRef<HTMLDivElement, DateTimeBlockDayPropsInterface>(
  (props: DateTimeBlockDayPropsInterface, forwardedRef) => {
    const { children, css: cssProp, date, ...rest } = props;

    const { selectedDate, onDateChange, disabledDates } = useContext(DateTimeBlockRootContext);

    const isDisabled = useDateTimeBlockDisabledItems(disabledDates);

    const handleDateChange = useCallback(() => {
      if (onDateChange) {
        onDateChange(date);
      }
    }, [onDateChange, date]);

    const isDateSelected = useCallback(
      (date: Date | string) => {
        if (!selectedDate) return false;
        if (date instanceof Date && selectedDate instanceof Date) {
          return date.getTime() === selectedDate.getTime();
        }
        return date === selectedDate;
      },
      [selectedDate],
    );

    const formattedAriaLabel = useMemo(() => format(date, 'eeee, MMMM d'), [date]);
    const formattedDay = useMemo(() => format(date, 'EEE'), [date]);
    const formattedDate = useMemo(() => format(date, 'MMM d'), [date]);

    return (
      <ToggleCard
        ref={forwardedRef}
        data-c11n-component="DateTimeBlock.Day"
        data-c11n-version={__C11N_VERSION__}
        disabled={isDisabled(date)}
        aria-label={formattedAriaLabel}
        elevated={false}
        css={{ ...dateTimeBlockDayRecipe, ...cssProp }}
        body={
          <Fragment>
            <Text textStyle="heading-xs">{formattedDay}</Text>
            <Text textStyle="body-sm">{formattedDate}</Text>
          </Fragment>
        }
        onClick={() => !isDisabled(date) && handleDateChange()}
        outlined
        selected={isDateSelected(date)}
        tone="neutral"
        {...rest}
      >
        {children}
      </ToggleCard>
    );
  },
);

DateTimeBlockDay.displayName = 'DateTimeBlock.Day';
